import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { UserState } from '../models/user.state';

export interface AdminRoutePermission {
    path: string;
    featureSwitch: FeatureSwitchName;
    permissionKey: string[];
}

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    constructor(private featureService: FeatureService, private store: Store) {}

    // Full list of admin routes and their access configuration
    private adminRoutes: AdminRoutePermission[] = [
        {
            path: '/admin/dashboard',
            featureSwitch: FeatureSwitchName.TenantDashboard,
            permissionKey: ['admin_dashboard_read'],
        },
        {
            path: '/admin/course-offerings',
            featureSwitch: null,
            permissionKey: ['admin_courses_read', 'admin_courses_write'],
        },
        {
            path: '/admin/all-modules',
            featureSwitch: null,
            permissionKey: ['admin_modules_read', 'admin_modules_write'],
        },
        {
            path: '/admin/users',
            featureSwitch: null,
            permissionKey: ['admin_users_read', 'admin_users_write', 'admin_passwordless_access_write'],
        },
        {
            path: '/admin/grow',
            featureSwitch: FeatureSwitchName.Engage,
            permissionKey: ['admin_grow_write'],
        },
        {
            path: '/admin/question-library',
            featureSwitch: FeatureSwitchName.ClasstimeSections,
            permissionKey: ['admin_question_library_write'],
        },
        {
            path: '/admin/reports',
            featureSwitch: FeatureSwitchName.Reporting,
            permissionKey: ['admin_reports_write'],
        },
        {
            path: '/admin/people-development',
            featureSwitch: null, // TODO: think about as it can be multiple
            permissionKey: ['admin_teams_write', 'admin_certification_write', 'admin_performance_review_write'],
        },
        {
            path: '/admin/settings',
            featureSwitch: null,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/tags',
            featureSwitch: null,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/image-library/my-images',
            featureSwitch: null,
            permissionKey: ['admin_settings_write'],
        },
        // TODO: Not sure if only gradebook needs to be active, or certificate management or...
        {
            path: '/admin/settings/certificates',
            featureSwitch: FeatureSwitchName.Gradebook,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/zoom',
            featureSwitch: FeatureSwitchName.Zoom,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/locations',
            featureSwitch: null,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/user-property',
            featureSwitch: FeatureSwitchName.ExtendedAttributes,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/offering-property',
            featureSwitch: FeatureSwitchName.ExtendedAttributes,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/execution-property',
            featureSwitch: FeatureSwitchName.ExtendedAttributes,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/survey',
            featureSwitch: FeatureSwitchName.SurveyIntegration,
            permissionKey: ['admin_settings_write'],
        },
        {
            path: '/admin/settings/training-approval-request/request-form',
            featureSwitch: FeatureSwitchName.TrainingApprovalRequest,
            permissionKey: ['admin_settings_write'],
        },
        // TODO: add routes for encryption_whitelist_management and encryption_key_management (shown in settings-tab-sidebar)
    ];

    /**
     * Checks if the user has any of the specified permission keys
     */
    private hasPermission(keys: string[]): boolean {
        const userPermissions = this.store.selectSnapshot(UserState.getMyInfo).permissions || [];

        // If user has 'tenant_management', grant access regardless of keys
        if (userPermissions.includes('tenant_management')) {
            return true;
        }

        // Return true if keys is empty or user has at least one of the permissions in keys
        return keys.length === 0 || keys.some((key) => userPermissions.includes(key));
    }

    /**
     * Checks if a specific route is allowed (based on feature and permissions)
     */
    hasAccess(path: string): boolean {
        const route = this.adminRoutes.find((r) => r.path === path);
        if (!route) return false;

        const isFeatureActive = route.featureSwitch ? this.featureService.checkFeatureStatus(route.featureSwitch) : true;
        const hasPermission = this.hasPermission(route.permissionKey);

        return isFeatureActive && hasPermission;
    }

    /**
     * Get the first available and permitted route
     */
    getFirstAvailableRoute(): string | null {
        for (const route of this.adminRoutes) {
            if (this.hasAccess(route.path)) {
                return route.path;
            }
        }
        return '/grow';
    }
}
